
export default [
  {
    "title": "Anchor text, explained (and how to get the mix right)",
    "description": "Anchor text is the clickable words in a link, and it tells search engines what the page behind it is about. Here are the five types, what a natural mix actually looks like, why chasing a perfect ratio backfires, and how to check yours.",
    "tag": "Backlinks 101",
    "author": "Brad Williams",
    "date": "2026-07-29",
    "draft": false,
    "image": "/img/og/anchor-text.png",
    "faqs": [
      {
        "q": "What is anchor text?",
        "a": "Anchor text is the visible, clickable words in a hyperlink. In the sentence \"read our guide to backlinks,\" the words \"guide to backlinks\" are the anchor text. Search engines read it as a description of the page being linked to, which is why it carries meaning beyond the link itself. It is one of the oldest relevance signals on the web and still one of the most direct."
      },
      {
        "q": "What are the types of anchor text?",
        "a": "Five categories cover almost everything. Branded uses your company name. Exact match is the precise keyword you want to rank for. Partial match is a longer phrase containing that keyword. Generic is filler like click here or read more. Naked is the raw URL itself. A healthy profile contains all five, weighted heavily toward branded and naked."
      },
      {
        "q": "What is exact match anchor text?",
        "a": "Exact match anchor text is a link whose clickable words are precisely the keyword you are targeting, such as a link reading \"competitor backlink checker\" pointing at a page about competitor backlink checkers. It is the most powerful type and the most dangerous, because real people rarely link that way naturally. A profile heavy in exact match is the clearest signal that links were built rather than earned."
      },
      {
        "q": "What is a good anchor text ratio?",
        "a": "There is no published ratio and anyone quoting exact percentages is guessing. What is observable is the shape of profiles that look natural, which are dominated by branded and naked anchors, contain a good share of generic and partial ones, and include exact match only occasionally. Rather than chasing a number, compare your distribution to two or three competitors who rank well and are not being penalised."
      },
      {
        "q": "Can anchor text hurt your rankings?",
        "a": "It can, though the mechanism is narrower than most people fear. The problem is not any single anchor but an unnatural pattern across the whole profile, specifically a high concentration of exact match commercial keywords. Google's Penguin work targeted exactly this. Ordinary sites earning links normally will never trip it, because nobody writing about you naturally picks your target keyword every time."
      },
      {
        "q": "How do I check the anchor text of my backlinks?",
        "a": "Three ways. Google Search Console shows top linking text under Links for a property you own. Any backlink tool that reports per-link data lists the anchor beside each link. For a single page, viewing the source and finding the link shows the exact wording. For competitors you need a tool with per-link data, since Search Console only ever covers sites you have verified."
      }
    ],
    "related": [
      {
        "title": "What is a backlink?",
        "to": "/guides/what-is-a-backlink",
        "description": "The wider concept anchor text sits inside."
      },
      {
        "title": "Types of backlinks, with examples",
        "to": "/guides/types-of-backlinks",
        "description": "The link categories that tend to produce each anchor type."
      },
      {
        "title": "Dofollow vs nofollow backlinks",
        "to": "/guides/dofollow-vs-nofollow-backlinks",
        "description": "The other attribute that decides what a link passes."
      },
      {
        "title": "How to analyze a competitor's backlink profile",
        "to": "/guides/how-to-analyze-competitor-backlinks",
        "description": "Read a rival's anchor mix as part of the wider picture."
      }
    ],
    "slug": "anchor-text",
    "readingTime": "5 min read"
  },
  {
    "title": "Backlink building services, explained",
    "description": "What backlink building companies and agencies actually sell, what the going rates buy you, and the questions that separate a real service from a reseller with a spreadsheet.",
    "tag": "Strategy",
    "author": "Brad Williams",
    "date": "2026-07-29",
    "draft": false,
    "image": "/img/og/backlink-building-services-explained.png",
    "faqs": [
      {
        "q": "What is a backlink building service?",
        "a": "A backlink building service is a company you pay to increase the number of quality sites linking to yours. In practice the label covers three very different businesses. Some do genuine outreach and digital PR, earning links by pitching real publications. Some produce content designed to attract links. Some simply resell placements from a marketplace, which is the cheapest to operate and the least likely to work. The word service tells you nothing until you ask which of the three you are talking to."
      },
      {
        "q": "How much do backlink building services cost?",
        "a": "A modest outreach retainer typically starts around one thousand dollars a month, a serious one runs three to five thousand, and digital PR campaigns start higher again. Packages advertised well below that, particularly ones quoting a fixed number of links per month, are almost always reselling marketplace placements, because genuine outreach cannot be delivered profitably at that price."
      },
      {
        "q": "Are backlink building services worth it?",
        "a": "They are worth it once you have exhausted the free work and have budget to buy time rather than knowledge. They are not worth it as a substitute for having something worth linking to, because outreach amplifies whatever you already have and cannot manufacture a reason for someone to link to a thin site. Most small sites get more from working their competitors' link profiles themselves for a few months first."
      },
      {
        "q": "What is the difference between a backlink building agency and a freelancer?",
        "a": "Mostly capacity and price. An agency brings a team, process, and existing publisher relationships, and charges for all three. A good freelancer often does the same outreach for less, but with a lower ceiling on volume and a real risk of the work stopping when they take a holiday. For a first engagement the more useful question is not agency or freelancer but whether they do outreach at all or resell placements."
      },
      {
        "q": "How do I know if a link building company is legitimate?",
        "a": "Ask what happens to the links if you stop paying, ask to see live examples from the last quarter rather than a logo wall, and ask what they will not do. A company that guarantees a specific number of links per month is buying placements, since nobody can guarantee an editor says yes. A company with no answer to what they refuse to do has no standards to describe."
      },
      {
        "q": "Can I do link building myself instead?",
        "a": "Yes, and for most small sites the first several months of it are better spent in-house. The highest-probability targets are the sites already linking to your competitors, and finding them is free. Doing that work yourself also teaches you what a good target looks like, which is exactly the knowledge you need to judge whether an agency is doing a good job later."
      }
    ],
    "related": [
      {
        "title": "Should you buy backlinks?",
        "to": "/guides/should-you-buy-backlinks",
        "description": "The honest answer on paid links, and what is actually safe."
      },
      {
        "title": "How to get backlinks",
        "to": "/guides/how-to-get-backlinks",
        "description": "The free methods to exhaust before you hire anyone."
      },
      {
        "title": "How to do a backlink gap analysis",
        "to": "/guides/backlink-gap-analysis",
        "description": "Build the target list an agency would charge you to build."
      },
      {
        "title": "Best competitor backlink tools, compared",
        "to": "/best-tools/best-competitor-backlink-tools",
        "description": "What to use if you decide to do the work in-house."
      }
    ],
    "slug": "backlink-building-services-explained",
    "readingTime": "6 min read"
  },
  {
    "title": "How to do a backlink gap analysis (step by step)",
    "description": "A backlink gap analysis finds the sites linking to your competitors but not to you. Here is the exact method, how to score which gaps to chase first, and when to earn a link versus buy it.",
    "tag": "Strategy",
    "author": "Brad Williams",
    "date": "2026-07-21",
    "draft": false,
    "image": "/img/og/backlink-gap-analysis.png",
    "faqs": [
      {
        "q": "What is a backlink gap analysis?",
        "a": "A backlink gap analysis compares your backlink profile against your competitors to find referring domains that link to them but not to you. Because those sites already link to businesses like yours, they are the highest-probability link targets you can find. It turns \"who has the most links\" into the far more useful \"which sites link to my whole niche except me.\""
      },
      {
        "q": "Is there a free backlink gap analysis tool?",
        "a": "Yes. Our free competitor backlink checker maps who links to any competitor, and if you add your own domain it shows the gap directly, the referring domains linking to them but not to you. Free backlink tools typically surface a sample of the full link profile rather than every link, which is plenty when you are researching a handful of competitors rather than auditing at scale."
      },
      {
        "q": "How do I analyze a competitor's backlink gaps?",
        "a": "Pull the referring domains for three to five real search competitors, then find the domains that link to two or more of them but not to you. Filter out the low-quality links, score what is left by relevance, authority, and how realistically you can win each one, and work the easiest high-value targets first. The overlap across several competitors is where the repeatable opportunities are."
      },
      {
        "q": "How often should I run a backlink gap analysis?",
        "a": "Quarterly is enough for most sites, and monthly if you are in a competitive niche where rivals earn links quickly. The gap is not static. Competitors keep adding links, so a list you built six months ago has already gone stale. Re-running it surfaces the new domains that have started linking within your niche since your last pass."
      }
    ],
    "related": [
      {
        "title": "How to find your competitors' backlinks",
        "to": "/guides/find-competitor-backlinks",
        "description": "Start here if you have not pulled a competitor's link list yet."
      },
      {
        "title": "How to analyze a competitor's backlink profile",
        "to": "/guides/how-to-analyze-competitor-backlinks",
        "description": "The wider framework for reading a link profile, not just the gap."
      },
      {
        "title": "How to replicate a competitor's best backlinks",
        "to": "/guides/replicate-competitor-backlinks",
        "description": "Turn the gap list into links you actually earn."
      },
      {
        "title": "Best competitor backlink tools, compared",
        "to": "/best-tools/best-competitor-backlink-tools",
        "description": "Which tools make gap analysis fastest, and when free is enough."
      },
      {
        "title": "How to get backlinks",
        "to": "/guides/how-to-get-backlinks",
        "description": "Every method, with the gap list as step one."
      },
      {
        "title": "Backlink building services, explained",
        "to": "/guides/backlink-building-services-explained",
        "description": "What it costs to have someone else work the list."
      }
    ],
    "slug": "backlink-gap-analysis",
    "readingTime": "9 min read"
  },
  {
    "title": "Backlink glossary: every SEO link term, explained",
    "description": "The backlink vocabulary that matters, defined without jargon. Referring domains, Domain Rating, dofollow, anchor text, disavow and more each get a one-line definition and a link to the full explanation.",
    "tag": "Backlinks 101",
    "author": "Brad Williams",
    "date": "2026-07-19",
    "draft": false,
    "image": "/img/og/backlink-glossary.png",
    "faqs": [
      {
        "q": "What is a backlink in simple terms?",
        "a": "A backlink is a link on someone else's website that points to yours. Search engines treat each one a little like a recommendation, so links from trusted, relevant sites can help your pages rank. It's the single most important off-page signal in SEO."
      },
      {
        "q": "What is the difference between a link and a backlink?",
        "a": "They describe the same thing from different directions. Any clickable link from one page to another is a link; it's called a backlink when you're talking about it from the perspective of the page being linked to. Your backlinks are simply the links pointing at you."
      },
      {
        "q": "What does DR mean in SEO?",
        "a": "DR stands for Domain Rating, a 0 to 100 score that estimates how authoritative a website is based on the strength of its backlink profile. It's a third-party metric (popularized by Ahrefs), not a Google ranking factor, so treat it as a rough sort order rather than gospel."
      }
    ],
    "related": [
      {
        "title": "How to find your competitors' backlinks",
        "to": "/guides/find-competitor-backlinks",
        "description": "Put the vocabulary to work on a real link profile."
      },
      {
        "title": "Dofollow vs nofollow backlinks",
        "to": "/guides/dofollow-vs-nofollow-backlinks",
        "description": "The one distinction worth understanding in full."
      },
      {
        "title": "How to check who is linking to your website",
        "to": "/guides/check-who-links-to-your-website",
        "description": "See referring domains and authority on your own site."
      },
      {
        "title": "What is a backlink?",
        "to": "/guides/what-is-a-backlink",
        "description": "Start here if you want the core concept in full."
      },
      {
        "title": "Types of backlinks, with examples",
        "to": "/guides/types-of-backlinks",
        "description": "The categories behind the vocabulary."
      },
      {
        "title": "Anchor text, explained",
        "to": "/guides/anchor-text",
        "description": "One term that deserves more than a glossary entry."
      }
    ],
    "slug": "backlink-glossary",
    "readingTime": "3 min read"
  },
  {
    "title": "How to check backlinks using Google (free methods that still work)",
    "description": "Google retired the old link search operator years ago, but you can still uncover backlinks for free using Search Console, the right search operators, and a checker. Here's exactly what works in 2026, and what doesn't.",
    "tag": "Backlinks 101",
    "author": "Brad Williams",
    "date": "2026-07-19",
    "draft": false,
    "image": "/img/og/check-backlinks-with-google.png",
    "faqs": [
      {
        "q": "Does the Google \"link:\" operator still work?",
        "a": "No. Google officially deprecated the old link search operator years ago, and it now returns an incomplete, effectively useless sample. If a tutorial tells you to type \"link:yoursite.com\" into Google, it's out of date. Use Google Search Console for your own site and a backlink checker for any site."
      },
      {
        "q": "How can I check backlinks for free using Google?",
        "a": "For your own site, Google Search Console's Links report lists your top linking sites and pages at no cost. For any site, combine a free backlink checker with Google search operators like a bare domain search or \"intext:\" queries to surface mentions and citations. None of these need a paid subscription for light use."
      },
      {
        "q": "How do I find who mentions my brand without linking?",
        "a": "Search Google for your brand or domain in quotes, then add -site:yourdomain.com to exclude your own pages. The results are pages that mention you elsewhere. Unlinked mentions are one of the easiest backlinks to earn. The site already talked about you, so a quick email asking them to link is often all it takes."
      },
      {
        "q": "Is Google Search Console enough on its own?",
        "a": "For monitoring your own inbound links, it's a strong free start, but it only shows sites Google has crawled and only for properties you've verified. It can't show a competitor's links at all. Pair it with a backlink checker when you need a fuller picture or want to research any site but your own."
      }
    ],
    "related": [
      {
        "title": "How to check who is linking to your website",
        "to": "/guides/check-who-links-to-your-website",
        "description": "The complete inbound-link workflow, beyond Google alone."
      },
      {
        "title": "How to find your competitors' backlinks",
        "to": "/guides/find-competitor-backlinks",
        "description": "For links to sites you don't own, a checker is the only way."
      },
      {
        "title": "Where to get backlinks that actually count",
        "to": "/guides/where-to-get-backlinks",
        "description": "Turn unlinked mentions and citations into real links."
      }
    ],
    "slug": "check-backlinks-with-google",
    "readingTime": "4 min read"
  },
  {
    "title": "How to check who is linking to your website",
    "description": "A clear, jargon-free walkthrough for finding every site that links to yours. Learn what tools to use, how to read the list, and how to spot the links worth protecting and the ones worth disavowing.",
    "tag": "Backlinks 101",
    "author": "Brad Williams",
    "date": "2026-07-19",
    "draft": false,
    "image": "/img/og/check-who-links-to-your-website.png",
    "faqs": [
      {
        "q": "How do I find out who is linking to my website?",
        "a": "Paste your domain into a backlink checker and it returns your referring domains, which are the unique sites that link to you. Google Search Console also lists your top linking sites for free under Links → External links. Between the two you get both a fast public view and Google's own record."
      },
      {
        "q": "Can I see who links to my site in Google Search Console?",
        "a": "Yes. Open Search Console, choose your property, and go to the Links report. \"Top linking sites\" shows the domains that link to you most, and you can export the full list. It only covers sites Google has crawled and only for a property you've verified, so pair it with a backlink checker for a fuller picture."
      },
      {
        "q": "What is inbound link analysis?",
        "a": "Inbound link analysis is the practice of reviewing the links pointing to your site. You look at how many there are, how authoritative the sources are, what pages they point to, and whether any are toxic. The goal is to protect your best links, fix or disavow harmful ones, and spot pages that attract links so you can make more of them."
      },
      {
        "q": "How often should I check my backlinks?",
        "a": "For most sites, a monthly review is plenty. That is enough to catch lost links from your best sources and notice any sudden spike in spammy links. Set a reminder rather than checking daily; backlink profiles move slowly unless something unusual is happening."
      }
    ],
    "related": [
      {
        "title": "How to find your competitors' backlinks",
        "to": "/guides/find-competitor-backlinks",
        "description": "The same method, pointed at the sites you're trying to beat."
      },
      {
        "title": "How to check backlinks using Google search",
        "to": "/guides/check-backlinks-with-google",
        "description": "Free operators and Search Console, step by step."
      },
      {
        "title": "How to find and remove toxic backlinks",
        "to": "/guides/find-and-remove-toxic-backlinks",
        "description": "What to do about the harmful links you turn up."
      },
      {
        "title": "Dofollow vs nofollow backlinks",
        "to": "/guides/dofollow-vs-nofollow-backlinks",
        "description": "Which of your inbound links actually pass authority."
      }
    ],
    "slug": "check-who-links-to-your-website",
    "readingTime": "3 min read"
  },
  {
    "title": "How to check backlinks for a YouTube video",
    "description": "Yes, YouTube videos earn backlinks too. They come from the sites that embed or link to a video. Here's how to check them, why most are nofollow, and what that means for ranking a video.",
    "tag": "Backlinks 101",
    "author": "Brad Williams",
    "date": "2026-07-19",
    "draft": false,
    "image": "/img/og/check-youtube-video-backlinks.png",
    "faqs": [
      {
        "q": "How do I check backlinks for a YouTube video?",
        "a": "Paste the full YouTube video URL into a backlink checker just as you would a website. It returns the pages that link to or embed that video. You can also search Google for the video's title or URL in quotes to surface sites that reference it. Both reveal where a video is being shared and cited."
      },
      {
        "q": "Do YouTube videos have backlinks?",
        "a": "Yes. Any page that embeds a video or links to its URL is a backlink to that video. Popular videos accumulate embeds on blogs, news articles, and forums. Those links point to the YouTube watch page rather than your own site, so they build the video's reach more than your domain's authority."
      },
      {
        "q": "Are YouTube embed links dofollow or nofollow?",
        "a": "Most links from a YouTube embed and from social shares are nofollow, so they pass little standard ranking authority. They still matter. They drive views, signal popularity, and expand reach. For SEO value to your own site, add links to your site in the video description and on the pages that embed the video."
      }
    ],
    "related": [
      {
        "title": "How to check who is linking to your website",
        "to": "/guides/check-who-links-to-your-website",
        "description": "The same method for your own site's backlinks."
      },
      {
        "title": "Dofollow vs nofollow backlinks",
        "to": "/guides/dofollow-vs-nofollow-backlinks",
        "description": "Why most video links are nofollow, and why that's fine."
      },
      {
        "title": "How to check backlinks using Google",
        "to": "/guides/check-backlinks-with-google",
        "description": "Free search operators to find who references a video."
      }
    ],
    "slug": "check-youtube-video-backlinks",
    "readingTime": "2 min read"
  },
  {
    "title": "Do free backlink generators work?",
    "description": "Free backlink generators and makers promise hundreds of links in minutes. Here is what they actually create, why search engines ignore it, the cases where it does real damage, and what to do instead if your budget is genuinely zero.",
    "tag": "Backlinks 101",
    "author": "Brad Williams",
    "date": "2026-07-29",
    "draft": false,
    "image": "/img/og/do-free-backlink-generators-work.png",
    "faqs": [
      {
        "q": "Do free backlink generators work?",
        "a": "No, not in the sense of improving rankings. Generators create links on sites built solely to host links, usually auto-generated directories, expired guestbooks, and profile pages, and search engines identified those patterns many years ago. The links are typically nofollow, on pages with no traffic, and discounted to zero. The usual result is no change at all rather than a penalty, which is why the tools keep having satisfied-looking users."
      },
      {
        "q": "Are free backlink generators safe?",
        "a": "Mostly they are harmless because the links are ignored, but there are two real risks. Running one repeatedly can build a large, obviously artificial link pattern that looks like an attempt at manipulation, and some generators place your link on sites hosting genuinely bad neighbourhoods. The bigger practical cost is opportunity: hours spent generating worthless links are hours not spent on the few methods that work."
      },
      {
        "q": "What is a backlink generator?",
        "a": "A backlink generator, sometimes called a backlink maker, is a tool that automatically submits your URL to large numbers of websites to create links. Most target web directories, statistics and analysis sites that generate a page per domain queried, guestbooks, wikis with open editing, and forum profile fields. Nobody chose to link to you, which is precisely why the links carry no weight."
      },
      {
        "q": "Why do backlink checkers show the links if they do not count?",
        "a": "Because a checker reports what exists on the web, not what Google credits. A crawler finding a link on a page is a factual observation. Whether Google passes any ranking value through that link is a separate judgement you cannot see directly. This gap is what makes generators feel like they work, since the number in the tool genuinely does go up."
      },
      {
        "q": "Is there any free way to get real backlinks?",
        "a": "Yes, and the effective methods are free apart from your time. Find the sites already linking to your competitors and pitch them, since they have demonstrated they link to businesses like yours. Claim brand mentions that have no link attached. Replace broken links on resource pages with your equivalent page. All three convert far better than cold outreach and cost nothing."
      },
      {
        "q": "What should I do if I already used one?",
        "a": "Almost certainly nothing. Google ignores most low-quality links automatically and disavowing is rarely the right response. Only consider a disavow file if you received a manual action in Search Console, or if there is a large and obviously artificial pattern. Panic-disavowing sweeps up good links along with the bad and does more damage than leaving things alone."
      }
    ],
    "related": [
      {
        "title": "How to get backlinks",
        "to": "/guides/how-to-get-backlinks",
        "description": "The methods that actually work, ordered by return on effort."
      },
      {
        "title": "How to get backlinks for free",
        "to": "/guides/how-to-get-backlinks-for-free",
        "description": "Free tactics that produce links search engines count."
      },
      {
        "title": "How to find and remove toxic backlinks",
        "to": "/guides/find-and-remove-toxic-backlinks",
        "description": "What to do about harmful links, without over-reacting."
      },
      {
        "title": "Should you buy backlinks?",
        "to": "/guides/should-you-buy-backlinks",
        "description": "The paid version of the same question."
      }
    ],
    "slug": "do-free-backlink-generators-work",
    "readingTime": "5 min read"
  },
  {
    "title": "Dofollow vs nofollow backlinks, explained clearly",
    "description": "What the difference actually is, why nofollow links still matter more than most people think, and how to check which kind you're getting, without the jargon or the myths.",
    "tag": "Backlinks 101",
    "author": "Brad Williams",
    "date": "2026-07-19",
    "draft": false,
    "image": "/img/og/dofollow-vs-nofollow-backlinks.png",
    "faqs": [
      {
        "q": "What is the difference between dofollow and nofollow backlinks?",
        "a": "A dofollow link passes authority (\"link equity\") from the linking site to yours, helping your rankings. A nofollow link includes a rel=\"nofollow\" attribute that tells search engines not to pass that authority. Both are real links a person can click; the difference is only in the ranking signal they send."
      },
      {
        "q": "Do nofollow links help SEO?",
        "a": "Yes, more than their reputation suggests. Google treats nofollow as a hint rather than a strict rule, so some nofollow links may still carry weight. Beyond that, they drive referral traffic, build brand awareness, and often lead to dofollow links later. A natural profile contains both, and an all-dofollow profile looks manipulated."
      },
      {
        "q": "How do I check if a backlink is dofollow or nofollow?",
        "a": "View the page source and look at the link's rel attribute. A value of rel=\"nofollow\" (or \"sponsored\" or \"ugc\") means it doesn't pass standard authority, while no such attribute means it's dofollow. Most backlink checkers and browser SEO extensions label each link's follow status for you so you don't have to inspect the HTML."
      },
      {
        "q": "What are sponsored and UGC link attributes?",
        "a": "They're two newer rel values Google introduced alongside nofollow. rel=\"sponsored\" marks paid or affiliate links; rel=\"ugc\" marks user-generated content like comments and forum posts. All three signal that a link shouldn't pass normal ranking authority, and Google treats them as hints about the link's nature."
      },
      {
        "q": "Is there a free dofollow backlink checker?",
        "a": "Yes. Our free checker returns any domain's referring domains with dofollow or nofollow labelled on each one, with no account. It reports at the referring-domain level rather than per individual link, which is the more useful view when you are deciding which sites are worth pursuing."
      },
      {
        "q": "How do I check if a backlink is dofollow or nofollow in bulk?",
        "a": "Use a backlink checker rather than inspecting pages by hand. Viewing source tells you about one link at a time, which is fine for a single mention and impractical across a profile. A checker reports the split across every referring domain at once, so you can sort by which sites actually pass authority."
      },
      {
        "q": "Does a domain marked dofollow mean all its links pass authority?",
        "a": "No, and this trips people up. Most checkers summarise follow status per referring domain rather than per link, because a domain list is far more readable. A single site often sends both kinds, such as an editorial link from an article and a nofollow one from an author profile. A dofollow label means the site sends at least one followed link, not that every link from it passes authority."
      }
    ],
    "related": [
      {
        "title": "Where to get backlinks that actually count",
        "to": "/guides/where-to-get-backlinks",
        "description": "Which sources tend to give dofollow vs nofollow links."
      },
      {
        "title": "How to check who is linking to your website",
        "to": "/guides/check-who-links-to-your-website",
        "description": "See the follow status across your whole link profile."
      },
      {
        "title": "How to analyze a competitor's backlink profile",
        "to": "/guides/how-to-analyze-competitor-backlinks",
        "description": "Read follow status the right way when sizing up rivals."
      },
      {
        "title": "What is a backlink?",
        "to": "/guides/what-is-a-backlink",
        "description": "The wider definition this distinction sits inside."
      },
      {
        "title": "Types of backlinks, with examples",
        "to": "/guides/types-of-backlinks",
        "description": "Which link types are usually dofollow, and which are not."
      },
      {
        "title": "Anchor text, explained",
        "to": "/guides/anchor-text",
        "description": "The other link attribute worth understanding."
      }
    ],
    "slug": "dofollow-vs-nofollow-backlinks",
    "readingTime": "4 min read"
  },
  {
    "title": "How to find and remove toxic backlinks (without over-reacting)",
    "description": "Most backlink profiles have some junk, and most of it is harmless. Here's how to tell a genuinely toxic link from ordinary noise, when disavowing is worth it, and how to do it safely if it is.",
    "tag": "Strategy",
    "author": "Brad Williams",
    "date": "2026-07-19",
    "draft": false,
    "image": "/img/og/find-and-remove-toxic-backlinks.png",
    "faqs": [
      {
        "q": "What are toxic backlinks?",
        "a": "Toxic backlinks are links from spammy, manipulative, or irrelevant sites that can harm your rankings. Think link farms, private blog networks, hacked sites, and pages stuffed with unrelated outbound links. The key word is manipulative. An ordinary low-quality directory link is usually just noise, not a threat."
      },
      {
        "q": "Do I need to disavow toxic backlinks?",
        "a": "Usually not. Google is good at ignoring obvious spam on its own, and its guidance is to disavow only when you have a manual action, or a clear pattern of paid or manipulative links you can't get removed. For most sites, no disavow file is needed at all. Over-disavowing can do more harm than the links ever would."
      },
      {
        "q": "What is a Google disavow file?",
        "a": "A disavow file is a text file you submit through Google's Disavow Tool listing domains or URLs you want Google to ignore when assessing your site. It's a last resort, not routine maintenance. Used carelessly it can remove authority from links that were actually helping you, so it should be a deliberate, evidence-based decision."
      },
      {
        "q": "Can competitors hurt me with toxic backlinks?",
        "a": "So-called negative SEO, which means pointing spammy links at a rival, is far less effective than it sounds. Google's systems are built to discount low-quality links rather than penalize the target for them. Monitor for sudden unnatural spikes so you're not caught off guard, but don't lose sleep over it or disavow reflexively."
      }
    ],
    "related": [
      {
        "title": "How to check who is linking to your website",
        "to": "/guides/check-who-links-to-your-website",
        "description": "The audit that surfaces toxic links in the first place."
      },
      {
        "title": "Where to get backlinks that actually count",
        "to": "/guides/where-to-get-backlinks",
        "description": "Build a clean profile so junk never dominates it."
      },
      {
        "title": "Dofollow vs nofollow backlinks",
        "to": "/guides/dofollow-vs-nofollow-backlinks",
        "description": "Why many \"scary\" links pass no authority anyway."
      },
      {
        "title": "Do free backlink generators work?",
        "to": "/guides/do-free-backlink-generators-work",
        "description": "Where most of these links came from in the first place."
      },
      {
        "title": "Should you buy backlinks?",
        "to": "/guides/should-you-buy-backlinks",
        "description": "The paid habit that creates profiles needing a cleanup."
      },
      {
        "title": "Anchor text, explained",
        "to": "/guides/anchor-text",
        "description": "Anchor patterns are the real negative-SEO signal."
      }
    ],
    "slug": "find-and-remove-toxic-backlinks",
    "readingTime": "3 min read"
  },
  {
    "title": "How to find your competitors' backlinks, without the jargon",
    "description": "You don't need a $129-a-month subscription to see where a competitor's links come from. Here's exactly how to do it, what the numbers actually mean, and when a free approach is genuinely enough.",
    "tag": "Backlinks 101",
    "author": "Brad Williams",
    "date": "2026-07-01",
    "updated": "2026-07-19",
    "draft": false,
    "image": "/img/og/find-competitor-backlinks.png",
    "faqs": [
      {
        "q": "How do I find a competitor's backlinks for free?",
        "a": "Paste the competitor's domain into a backlink checker, sort the referring domains by authority, and look at the top 20. Those top links tell the story. Free checkers cap how many links you see, but that cap is rarely the thing holding you back when you're researching a handful of competitors a month."
      },
      {
        "q": "Is it legal to look at a competitor's backlinks?",
        "a": "Yes. Backlinks are public. They're just links on other websites that anyone can see. Backlink tools crawl the open web the same way search engines do. You're reading a map that's already published, not accessing anything private."
      },
      {
        "q": "How many competitor backlinks should I try to copy?",
        "a": "Focus on quality, not count. Twenty replicable links from sites your buyers actually read will move rankings more than a thousand from directories nobody visits. Start with the referring domains you could plausibly earn a link from this quarter."
      },
      {
        "q": "What's the difference between a backlink and a referring domain?",
        "a": "A backlink is a single link pointing to a site. A referring domain is a unique website linking to it, so ten links from one site count as just one referring domain. Referring domains are the more honest measure of how many distinct sources vouch for a page."
      },
      {
        "q": "Which competitors should I check?",
        "a": "Check the sites already ranking on page one for the terms you want, not the companies you think of as rivals. Your business competitor and your search competitor are often different, and it's the search competitor whose links are actually affecting your rankings. Three to five is the right number to start with."
      },
      {
        "q": "How do I view a competitor's backlinks without a subscription?",
        "a": "Paste their domain into a free checker and read the referring domains it returns. Free tools show you a sample rather than every link, which sounds like a limitation but rarely is, because the strongest referring domains are the ones that matter and they sort to the top anyway."
      },
      {
        "q": "How do I check the backlinks of several competitors at once?",
        "a": "Run them one at a time, then compare the lists and look for overlap. A domain linking to two or three of your competitors but not to you is the highest-probability target you will find, because it has already demonstrated it links to businesses in your category. That overlap is what a backlink gap analysis finds for you automatically."
      },
      {
        "q": "What if a competitor has thousands of backlinks?",
        "a": "Ignore almost all of them. Sort by authority and read the top twenty, which is where the story is. A profile of forty thousand links is usually a few hundred meaningful relationships plus a very long tail of directories, scrapers, and syndication that nobody needs to act on."
      }
    ],
    "related": [
      {
        "title": "How to check who is linking to your website",
        "to": "/guides/check-who-links-to-your-website",
        "description": "Point the same method at your own domain to audit your inbound links."
      },
      {
        "title": "How to analyze a competitor's backlink profile",
        "to": "/guides/how-to-analyze-competitor-backlinks",
        "description": "Turn a raw link list into a prioritized action plan."
      },
      {
        "title": "How to replicate a competitor's best backlinks",
        "to": "/guides/replicate-competitor-backlinks",
        "description": "The step-by-step for actually earning the links you find."
      },
      {
        "title": "Best competitor backlink tools, compared",
        "to": "/best-tools/best-competitor-backlink-tools",
        "description": "What each tool is genuinely good at, and when free is enough."
      },
      {
        "title": "What is a backlink?",
        "to": "/guides/what-is-a-backlink",
        "description": "The foundation, if any of the vocabulary is new."
      },
      {
        "title": "How to get backlinks",
        "to": "/guides/how-to-get-backlinks",
        "description": "What to do once you have the competitor's list."
      }
    ],
    "slug": "find-competitor-backlinks",
    "readingTime": "5 min read"
  },
  {
    "title": "How to analyze a competitor's backlink profile",
    "description": "Pulling a competitor's links is easy. Turning that raw list into a prioritized plan is the part that actually moves rankings. Here's the framework, covering link gaps, authority tiers, and the links worth your time.",
    "tag": "Strategy",
    "author": "Brad Williams",
    "date": "2026-07-19",
    "draft": false,
    "image": "/img/og/how-to-analyze-competitor-backlinks.png",
    "faqs": [
      {
        "q": "What is competitor backlink analysis?",
        "a": "Competitor backlink analysis means examining the links pointing to a competitor's site to understand how they earned their authority. You look at which sites link to them, what kind of links those are, and which of those links you could realistically earn too. Done well, it turns a rival's link profile into a to-do list for your own."
      },
      {
        "q": "What should I look at first in a competitor's backlinks?",
        "a": "Start with referring domains sorted by authority, not total link count. A referring domain is simply a unique website that links to them. The top 20 most authoritative domains tell you where their strength comes from. Then look at link type (editorial, resource page, guest post) to understand how each was earned. Finally, look at link gaps, meaning sites that link to several competitors but not to you."
      },
      {
        "q": "What is a link gap analysis?",
        "a": "A link gap analysis compares the backlink profiles of several competitors to find domains that link to two or more of them but not to you. Those sites already link within your niche and clearly link to more than one player, which makes them some of the highest-probability link targets you can find."
      },
      {
        "q": "What is competitive backlink analysis?",
        "a": "Competitive backlink analysis and competitor backlink analysis are the same practice under two names, and you'll see both used interchangeably. Either way it means comparing the link profiles of the sites you compete with in search, finding where their authority comes from, and identifying which of those sources you could realistically earn a link from as well. The word competitive is the more common phrasing in agency work, competitor in tool marketing."
      },
      {
        "q": "How many competitors should I analyze?",
        "a": "Three to five is the sweet spot. One competitor shows you their tactics. Looking at several reveals the patterns: the sites, publications, and resource pages that link across your whole niche. Those recurring sources are where the real opportunities are, because a link there tends to be repeatable."
      }
    ],
    "related": [
      {
        "title": "How to find your competitors' backlinks",
        "to": "/guides/find-competitor-backlinks",
        "description": "The step before this one, where you pull the raw link list."
      },
      {
        "title": "How to replicate a competitor's best backlinks",
        "to": "/guides/replicate-competitor-backlinks",
        "description": "The step after, where you actually earn the links you've prioritized."
      },
      {
        "title": "Where to get backlinks that actually count",
        "to": "/guides/where-to-get-backlinks",
        "description": "The link types worth targeting once you know the gaps."
      },
      {
        "title": "Best competitor backlink tools, compared",
        "to": "/best-tools/best-competitor-backlink-tools",
        "description": "Which tool makes link-gap analysis fastest."
      },
      {
        "title": "Types of backlinks, with examples",
        "to": "/guides/types-of-backlinks",
        "description": "Classify what you find while reading a profile."
      },
      {
        "title": "Anchor text, explained",
        "to": "/guides/anchor-text",
        "description": "Read a rival's anchor mix without chasing ratios."
      }
    ],
    "slug": "how-to-analyze-competitor-backlinks",
    "readingTime": "3 min read"
  },
  {
    "title": "How to get backlinks for free (without the spammy shortcuts)",
    "description": "Free backlink generators promise thousands of links in minutes. Here's why those do nothing (or harm you), and the genuinely free tactics that earn links search engines actually count.",
    "tag": "Link building",
    "author": "Brad Williams",
    "date": "2026-07-19",
    "draft": false,
    "image": "/img/og/how-to-get-backlinks-for-free.png",
    "faqs": [
      {
        "q": "How do I get backlinks for free?",
        "a": "Earn them from relevant sources rather than generating them. Convert unlinked mentions into links, add yourself to niche resource pages you belong on, answer journalist requests, get on a few relevant podcasts, and claim legitimate profile and partner links. All are free and, unlike bulk generators, produce links search engines actually count."
      },
      {
        "q": "Which free link tactics should I avoid?",
        "a": "Skip anything automated. Bulk generators, comment and forum link dropping, and reciprocal link exchanges at scale all produce links on pages nobody chose to publish, which is exactly the category search engines discount. The time is better spent on a handful of real links. We cover why the generators keep appearing to work in a separate guide."
      },
      {
        "q": "What is the fastest free backlink to get?",
        "a": "Converting an unlinked mention is usually the fastest. When a site has already named your brand without linking, the editorial decision is done. A short, specific email asking them to turn the mention into a link often succeeds within days, because you're asking for a tiny change, not a favor."
      },
      {
        "q": "Can you rank without paying for backlinks?",
        "a": "Yes. Most sites that rank well never buy links. They earn them by being genuinely useful, being quoted by journalists, publishing things worth citing, and doing consistent, relevant outreach. Buying links violates Google's guidelines and risks a penalty, so free earned links are both safer and more durable."
      }
    ],
    "related": [
      {
        "title": "Where to get backlinks that actually count",
        "to": "/guides/where-to-get-backlinks",
        "description": "The full menu of link sources, ranked by effort."
      },
      {
        "title": "How to replicate a competitor's best backlinks",
        "to": "/guides/replicate-competitor-backlinks",
        "description": "Earn the same free links your rivals already have."
      },
      {
        "title": "How to check backlinks using Google",
        "to": "/guides/check-backlinks-with-google",
        "description": "Find the unlinked mentions you can convert for free."
      },
      {
        "title": "Dofollow vs nofollow backlinks",
        "to": "/guides/dofollow-vs-nofollow-backlinks",
        "description": "Which free links actually pass authority."
      },
      {
        "title": "How to get backlinks",
        "to": "/guides/how-to-get-backlinks",
        "description": "The full playbook, paid and free methods ranked together."
      },
      {
        "title": "Do free backlink generators work?",
        "to": "/guides/do-free-backlink-generators-work",
        "description": "Why the free shortcut everyone tries produces nothing."
      }
    ],
    "slug": "how-to-get-backlinks-for-free",
    "readingTime": "3 min read"
  },
  {
    "title": "How to get backlinks (the methods that still work)",
    "description": "Nine ways to earn backlinks, ordered by how much you get back for the effort. Start with the ones that use links your competitors already have, because those are the warmest targets you will ever work and they cost nothing but time.",
    "tag": "Strategy",
    "author": "Brad Williams",
    "date": "2026-07-29",
    "draft": false,
    "image": "/img/og/how-to-get-backlinks.png",
    "faqs": [
      {
        "q": "How do I get backlinks?",
        "a": "Start with the sites already linking to your competitors, because they have proven they link to businesses in your category and are the warmest targets available. Then claim what you are already owed, meaning unlinked brand mentions and broken links pointing at pages you could replace. Then cover the baseline of directory and citation listings. Only after those are exhausted does cold outreach and content creation become the best use of your time."
      },
      {
        "q": "How do I get a backlink to my site for free?",
        "a": "Every method that reliably works is free apart from your time. Find the referring domains of two or three competitors, look for sites linking to several of them but not to you, and pitch those. Search for mentions of your brand that have no link attached and ask for one. Both cost nothing and convert far better than cold outreach, because the target has already demonstrated interest in your category or in you specifically."
      },
      {
        "q": "How long does it take to get backlinks?",
        "a": "The baseline listings take an afternoon. Unlinked mention and broken link recovery usually produce results within two to three weeks, since you are asking for something small from someone already predisposed to say yes. Cold outreach for editorial links runs on a six to twelve week cycle and converts in low single-digit percentages, which is why it belongs after the easier work rather than before it."
      },
      {
        "q": "How many links should I aim for per month?",
        "a": "Pace matters less than consistency and source quality, so treat this as a workload question rather than a target. A single person working the methods here part time realistically lands a handful of meaningful referring domains a quarter, front-loaded because the first three tactics convert fastest. If you want to know how many you need in total, benchmark against whoever currently outranks you rather than against a number from a blog post."
      },
      {
        "q": "Do I need to pay for backlinks?",
        "a": "No, and the free methods generally outperform the paid ones for small sites. Paying makes sense once you have exhausted the free work and your constraint is hours rather than knowledge, at which point you are buying outreach labour rather than link placements. Buying links directly is against Google's guidelines and the cheap end of that market sells links that carry no value."
      },
      {
        "q": "What is the fastest backlink to get?",
        "a": "An unlinked brand mention. Somebody has already written your name in an article, which means the hard part, deciding you are worth referencing, is done. Asking them to make it a link is a small request with a high success rate, and it often takes one email. Search your brand name in quotes, exclude your own domain, and work the results."
      }
    ],
    "related": [
      {
        "title": "How to get backlinks for free",
        "to": "/guides/how-to-get-backlinks-for-free",
        "description": "The free tactics in depth, without the spammy shortcuts."
      },
      {
        "title": "Where to get backlinks that actually count",
        "to": "/guides/where-to-get-backlinks",
        "description": "The specific places worth pursuing, ranked."
      },
      {
        "title": "How to replicate a competitor's best backlinks",
        "to": "/guides/replicate-competitor-backlinks",
        "description": "The single highest-probability method, step by step."
      },
      {
        "title": "Should you buy backlinks?",
        "to": "/guides/should-you-buy-backlinks",
        "description": "When paying is legitimate, and when it wastes money."
      }
    ],
    "slug": "how-to-get-backlinks",
    "readingTime": "6 min read"
  },
  {
    "title": "How to monitor competitor backlinks (and your own) over time",
    "description": "Finding a competitor's links is a snapshot. Monitoring is the movie: the new links they win, the ones they lose, and the moment to act. Here's how to set up backlink monitoring without drowning in alerts.",
    "tag": "Strategy",
    "author": "Brad Williams",
    "date": "2026-07-19",
    "draft": false,
    "image": "/img/og/how-to-monitor-competitor-backlinks.png",
    "faqs": [
      {
        "q": "How do I monitor competitor backlinks?",
        "a": "Set a baseline of a competitor's referring domains, then re-check on a schedule (monthly is plenty for most) and watch two lists. One is the new links they've gained, and the other is the links they've lost. A monitoring tool automates the re-check and alerts you to changes, so you're reacting to fresh wins instead of manually re-pulling reports."
      },
      {
        "q": "How often should I check competitor backlinks?",
        "a": "Monthly suits most sites and niches. Check weekly only if you're in a fast-moving space or running an active campaign where a competitor's new link could change your plan within days. Daily monitoring is rarely worth the noise unless you're managing many domains or a large link-building operation."
      },
      {
        "q": "What is backlink monitoring?",
        "a": "Backlink monitoring is tracking a link profile over time rather than viewing it once. It records which referring domains are new, which were lost, and how the overall profile is trending. Applied to competitors it reveals their live strategy; applied to your own site it protects your best links and flags unnatural spikes early."
      },
      {
        "q": "What is the difference between a backlink audit and backlink monitoring?",
        "a": "An audit is a one-time deep review of a profile's current state, covering quality, toxicity, and structure. Monitoring is the ongoing watch for changes to that profile. You audit to understand where things stand today, and you monitor so you notice when they change tomorrow."
      },
      {
        "q": "How do I monitor my own backlinks?",
        "a": "Exactly the same way, pointed at your own domain. Take a baseline of your referring domains, re-check monthly, and read only the diff. Watching your own profile is defensive rather than offensive, since the point is catching a strong link dropping off while it is still recoverable. Google Search Console is a useful second source here because it reflects what Google itself has counted."
      },
      {
        "q": "How do I recover a lost backlink?",
        "a": "Work out why it went before you email anyone, because the four causes need different messages. If the page was deleted or redesigned the loss is usually accidental and a polite note often restores it. If the link was removed deliberately, ask why before pushing. If your own URL moved or broke, fix the redirect and no email is needed at all. If the whole site went down, there is nothing to recover."
      },
      {
        "q": "What should I do when a competitor gains a new link?",
        "a": "Look at how they earned it rather than at the link itself. A new referring domain is evidence of a live campaign, so the question worth answering is whether it came from a guest post, a resource page, a piece of press, or a tool mention. Whatever produced it is probably still running, which means the same route is likely still open to you."
      }
    ],
    "related": [
      {
        "title": "How to find your competitors' backlinks",
        "to": "/guides/find-competitor-backlinks",
        "description": "Get the baseline snapshot before you monitor changes."
      },
      {
        "title": "How to analyze a competitor's backlink profile",
        "to": "/guides/how-to-analyze-competitor-backlinks",
        "description": "Turn the changes you spot into a prioritized plan."
      },
      {
        "title": "Best backlink monitoring & management software",
        "to": "/best-tools/backlink-monitoring-tools",
        "description": "The tools that automate the re-check and alerts."
      },
      {
        "title": "How to replicate a competitor's best backlinks",
        "to": "/guides/replicate-competitor-backlinks",
        "description": "Act on the new links a competitor just won."
      },
      {
        "title": "How to get backlinks",
        "to": "/guides/how-to-get-backlinks",
        "description": "Turn what you spot them earning into your own links."
      }
    ],
    "slug": "how-to-monitor-competitor-backlinks",
    "readingTime": "5 min read"
  },
  {
    "title": "How to replicate a competitor's best backlinks",
    "description": "\"Stealing\" a competitor's backlinks really means earning a link from the same source. Here's the repeatable process: which links to target, how to pitch them, and which ones to leave alone.",
    "tag": "Strategy",
    "author": "Brad Williams",
    "date": "2026-07-19",
    "draft": false,
    "image": "/img/og/replicate-competitor-backlinks.png",
    "faqs": [
      {
        "q": "Can you really steal a competitor's backlinks?",
        "a": "Not literally. You can't remove a link from someone else's site. What people mean by \"stealing\" backlinks is earning a link from the same source that links to your competitor. If a resource page or publication links to them, you find the person who controls that page and give them a reason to link to you too."
      },
      {
        "q": "How do you replicate a competitor's backlinks?",
        "a": "Pull their referring domains, filter to the links you could realistically earn (resource pages, guest posts, roundups, unlinked mentions), find the right contact for each, and pitch a specific reason to add or switch the link. Broken-link building and \"link intersect\" gaps are the highest-yield versions of this."
      },
      {
        "q": "What is broken link building?",
        "a": "Broken link building is finding a page that links to a competitor's URL that no longer works, then offering your working page as the replacement. The site owner wins by fixing a dead link, and you win the link. It's one of the highest-conversion outreach tactics because you're solving a real problem for them."
      },
      {
        "q": "Which competitor backlinks are not worth copying?",
        "a": "Skip low-quality directories, paid link networks, spammy blog comments, and links from sites unrelated to your niche. They add little authority and some carry risk. Focus your effort on editorial mentions, curated resource lists, and relevant guest posts. Those are the links that actually moved your competitor's rankings."
      }
    ],
    "related": [
      {
        "title": "How to analyze a competitor's backlink profile",
        "to": "/guides/how-to-analyze-competitor-backlinks",
        "description": "Build the prioritized target list this guide acts on."
      },
      {
        "title": "Where to get backlinks that actually count",
        "to": "/guides/where-to-get-backlinks",
        "description": "The link sources worth pitching, ranked by effort."
      },
      {
        "title": "How to find your competitors' backlinks",
        "to": "/guides/find-competitor-backlinks",
        "description": "The starting point. Pull the profile you'll mine."
      },
      {
        "title": "How to get backlinks",
        "to": "/guides/how-to-get-backlinks",
        "description": "The wider playbook this tactic belongs to."
      },
      {
        "title": "Should you buy backlinks?",
        "to": "/guides/should-you-buy-backlinks",
        "description": "When paying beats earning, and when it does not."
      }
    ],
    "slug": "replicate-competitor-backlinks",
    "readingTime": "3 min read"
  },
  {
    "title": "Should you buy backlinks? An honest answer",
    "description": "Buying backlinks is against Google's guidelines, and most of the market selling them is selling you a penalty. Here is what actually happens when you buy, which paid work is legitimate, and how to tell the difference before you spend anything.",
    "tag": "Strategy",
    "author": "Brad Williams",
    "date": "2026-07-29",
    "draft": false,
    "image": "/img/og/should-you-buy-backlinks.png",
    "faqs": [
      {
        "q": "Should you buy backlinks?",
        "a": "Usually no, at least not the way the term is normally meant. Buying a link purely to pass ranking authority violates Google's link spam policies, and the cheap end of that market sells links on sites that exist only to sell links, which carry no value and some risk. What is legitimate is paying people to do work that earns links, such as outreach, digital PR, or building the baseline profile of directory and citation listings that every business needs. The distinction is whether you are paying for the link itself or for the labour."
      },
      {
        "q": "Will buying backlinks get my site penalized?",
        "a": "It can, though not usually in the dramatic way people imagine. Manual penalties for link schemes exist but are rare for small sites. The far more common outcome is that Google simply ignores the links, so you paid for nothing. The real risk sits at the extremes, meaning large volumes of obviously paid links, links from networks Google has already identified, or anchor text so over-optimised it announces itself."
      },
      {
        "q": "How much do backlinks cost?",
        "a": "Legitimate paid link work generally runs from a few hundred dollars a month for a modest outreach retainer into the thousands for digital PR. Marketplace listings selling individual links typically advertise anywhere from a few dollars to a few hundred per link, and price there correlates far more with the seller's confidence than with the link's value. If a link costs five dollars, it is worth less than that."
      },
      {
        "q": "What is the difference between buying backlinks and paying for link building?",
        "a": "Buying a backlink means paying a site owner to place a link, which is the transaction Google's guidelines prohibit. Paying for link building means hiring someone to do the work of earning links, including research, outreach, content, and follow-up. The output can look similar from the outside, but one buys placement and the other buys effort, and only the second is defensible if anyone asks."
      },
      {
        "q": "Are paid directory and citation links safe?",
        "a": "Broadly yes, because they are not really link buying. Business directories, industry associations, and local citation sites charge for a listing rather than for ranking authority, most of them mark their links nofollow, and their value is discovery and trust signals rather than PageRank. This is the one corner of paid links that is both cheap and safe, which is exactly why it is the baseline rather than the strategy."
      },
      {
        "q": "What should I do instead of buying backlinks?",
        "a": "Find out which sites already link to your competitors and go earn from that list. Those sites have demonstrated they link to businesses in your category, which makes them dramatically warmer targets than cold outreach, and the work is free apart from your time. Start with a backlink gap analysis, then work the overlap between several competitors."
      }
    ],
    "related": [
      {
        "title": "Backlink building services, explained",
        "to": "/guides/backlink-building-services-explained",
        "description": "What agencies actually sell, what it costs, and how to vet one."
      },
      {
        "title": "How to get backlinks",
        "to": "/guides/how-to-get-backlinks",
        "description": "The free methods that work, in the order worth trying them."
      },
      {
        "title": "How to do a backlink gap analysis",
        "to": "/guides/backlink-gap-analysis",
        "description": "The highest-probability targets you can earn without paying."
      },
      {
        "title": "How to find and remove toxic backlinks",
        "to": "/guides/find-and-remove-toxic-backlinks",
        "description": "What to do if you have already bought links you regret."
      }
    ],
    "slug": "should-you-buy-backlinks",
    "readingTime": "7 min read"
  },
  {
    "title": "Types of backlinks, with examples",
    "description": "The main types of backlink, what each one looks like in the wild, roughly what it is worth, and how hard it is to get. Sorted so you can tell the links worth chasing from the ones that waste an afternoon.",
    "tag": "Backlinks 101",
    "author": "Brad Williams",
    "date": "2026-07-29",
    "draft": false,
    "image": "/img/og/types-of-backlinks.png",
    "faqs": [
      {
        "q": "What are the main types of backlinks?",
        "a": "The types worth knowing are editorial links inside articles, resource and directory listings, guest post links, digital PR and citation links, profile links, forum and comment links, and image or embed links. They differ enormously in value. Editorial and PR links are the strongest and hardest to get, directory and profile links are easy and mostly serve as a baseline rather than a ranking lever."
      },
      {
        "q": "What is an example of a backlink?",
        "a": "A trade publication writes an article about pricing strategy, references a survey you published, and links to your research page. That link is a backlink, and specifically an editorial one, which is the most valuable kind because a person decided independently that your page was worth citing. A listing of your business in an industry directory is also a backlink, just a much weaker one."
      },
      {
        "q": "Which type of backlink is most valuable?",
        "a": "Editorial links inside the body of a relevant, well-read article. They are valuable precisely because they cannot be requested into existence reliably, which is what makes them credible. A single editorial link from a publication your customers read will usually outperform dozens of directory and profile links combined."
      },
      {
        "q": "Are all backlinks good for SEO?",
        "a": "No. Links from sites built solely to host links carry no value and, in volume, can look like an attempt at manipulation. Most low-quality links are simply ignored rather than punished, so the usual cost is wasted effort rather than a penalty. The links to be genuinely wary of are large numbers from unrelated, low-authority sites appearing suddenly."
      },
      {
        "q": "What is a dofollow backlink?",
        "a": "A dofollow backlink is an ordinary link with no attribute telling search engines to discount it, so it passes ranking authority. The opposite is a nofollow link, marked with rel=\"nofollow\", which asks search engines not to treat it as an endorsement. Most editorial links are dofollow by default, while most directory, forum, and social profile links are nofollow."
      },
      {
        "q": "How do I know what type of backlink I have?",
        "a": "Look at where the link sits and how it is marked. If it is inside article text written by someone who chose to reference you, it is editorial. If it is in a list of businesses, it is a directory or citation link. Check the page source or use a backlink checker to see whether it is dofollow or nofollow, since that determines whether it passes authority."
      }
    ],
    "related": [
      {
        "title": "What is a backlink?",
        "to": "/guides/what-is-a-backlink",
        "description": "The definition, and why search engines weigh links so heavily."
      },
      {
        "title": "Dofollow vs nofollow backlinks",
        "to": "/guides/dofollow-vs-nofollow-backlinks",
        "description": "Which of these types actually pass ranking authority."
      },
      {
        "title": "Where to get backlinks that actually count",
        "to": "/guides/where-to-get-backlinks",
        "description": "The specific places worth pursuing each type."
      },
      {
        "title": "How to get backlinks",
        "to": "/guides/how-to-get-backlinks",
        "description": "The playbook for earning the higher-value types."
      },
      {
        "title": "Anchor text, explained",
        "to": "/guides/anchor-text",
        "description": "The other half of what a link tells search engines."
      }
    ],
    "slug": "types-of-backlinks",
    "readingTime": "6 min read"
  },
  {
    "title": "What is a backlink? A plain-English definition",
    "description": "A backlink is a link from someone else's website to yours. Here is what that actually means for SEO, why search engines care, what makes one backlink worth more than another, and how to see the ones you already have.",
    "tag": "Backlinks 101",
    "author": "Brad Williams",
    "date": "2026-07-29",
    "draft": false,
    "image": "/img/og/what-is-a-backlink.png",
    "faqs": [
      {
        "q": "What is a backlink?",
        "a": "A backlink is a link on another website that points to yours. If a blog writes about your product and links to your homepage, that link is a backlink from their site to yours. Search engines treat backlinks as signals of credibility, on the reasoning that people tend to link to pages they find useful, so a page with links from many trusted sites is likely to be worth showing."
      },
      {
        "q": "What does backlink mean in SEO?",
        "a": "In SEO, backlink means an inbound link used as a ranking signal. Google's original insight was that a link works like a citation, and a page cited by many respected sources is probably good. Backlinks remain one of the strongest ranking factors, though modern search engines weigh the quality, relevance, and context of each link far more than the raw number of them."
      },
      {
        "q": "Why are backlinks important?",
        "a": "Backlinks are important because they are one of the few ranking signals you cannot create for yourself. You control your own content and site structure, so those signals are cheap for anyone to fake. A link requires another person to decide you are worth referencing, which is exactly what makes it credible evidence and why it carries so much weight."
      },
      {
        "q": "What is the difference between a backlink and an internal link?",
        "a": "An internal link points from one page on your site to another page on the same site, and it helps search engines understand your structure and spread authority around. A backlink comes from a different website entirely. Internal links organise the authority you already have, backlinks are how you get more of it."
      },
      {
        "q": "How many backlinks do I need to rank?",
        "a": "There is no number, and any tool quoting one is guessing. What matters is how you compare to the pages currently ranking for your target term. If the top results have links from fifty distinct sites and you have three, that gap is your answer. Check the referring domains of whoever ranks above you and use that as the benchmark rather than an abstract target."
      },
      {
        "q": "Do backlinks still work in 2026?",
        "a": "Yes. Google has repeatedly reduced the weight of low-quality links and improved at ignoring manipulated ones, but links from relevant, credible sites remain a core signal. What has changed is that volume stopped working. A hundred links from sites nobody reads now does roughly nothing, while a handful from publications your customers actually read still moves rankings."
      }
    ],
    "related": [
      {
        "title": "Types of backlinks, with examples",
        "to": "/guides/types-of-backlinks",
        "description": "The main kinds of link and which ones are worth pursuing."
      },
      {
        "title": "Dofollow vs nofollow backlinks",
        "to": "/guides/dofollow-vs-nofollow-backlinks",
        "description": "Which links pass authority, and which still matter anyway."
      },
      {
        "title": "Backlink glossary",
        "to": "/guides/backlink-glossary",
        "description": "Every other term you will run into, defined once."
      },
      {
        "title": "How to check who is linking to your website",
        "to": "/guides/check-who-links-to-your-website",
        "description": "See the backlinks you already have."
      },
      {
        "title": "Anchor text, explained",
        "to": "/guides/anchor-text",
        "description": "The words in a link, and why they carry meaning."
      }
    ],
    "slug": "what-is-a-backlink",
    "readingTime": "6 min read"
  },
  {
    "title": "Where to get backlinks that actually count",
    "description": "Forget the \"1,000 free backlink sites\" lists. Most of those links do nothing, and some hurt. Here's where good links genuinely come from, ranked by effort, plus how to tell a link worth chasing from one worth ignoring.",
    "tag": "Link building",
    "author": "Brad Williams",
    "date": "2026-07-19",
    "draft": false,
    "image": "/img/og/where-to-get-backlinks.png",
    "faqs": [
      {
        "q": "What are the best sites to get backlinks from?",
        "a": "The best backlink sources are relevant sites your audience actually reads, where an editor decides what gets published. Think industry publications, resource pages in your niche, podcasts and interviews, and reputable directories specific to your field. A single link from a respected niche site outweighs hundreds from generic \"submit your URL\" directories."
      },
      {
        "q": "Do free backlink sites work?",
        "a": "Most of the \"free backlink sites\" lists you'll find are low-quality directories and profile pages that pass little or no authority, and some can even look manipulative to search engines. Free links from genuinely relevant sources do work. That includes resource pages, press coverage from reporter requests, and reaching out when a site mentions you without linking. What matters is relevance and whether a real editor chose the link, not the price."
      },
      {
        "q": "How do I get backlinks for a new website?",
        "a": "Start with links you can control or easily earn. That means a few relevant niche directories, your own profiles, and partners or suppliers who list their customers. Then move to links you have to earn. Get quoted by journalists, contribute a guest article, and reach out to resource pages you belong on. Avoid buying links in bulk. For a new site, that's the fastest way to look spammy."
      },
      {
        "q": "Are paid backlinks a good idea?",
        "a": "Buying links violates Google's guidelines and carries real risk. At best you waste money on worthless links. At worst you get hit with a manual penalty. The exception is legitimate paid placements that are clearly marked as sponsored, which pass no ranking value by design. For ranking, links you earn are both safer and far more effective."
      }
    ],
    "related": [
      {
        "title": "How to replicate a competitor's best backlinks",
        "to": "/guides/replicate-competitor-backlinks",
        "description": "The outreach playbook for earning the links below."
      },
      {
        "title": "Dofollow vs nofollow backlinks",
        "to": "/guides/dofollow-vs-nofollow-backlinks",
        "description": "Which of these sources actually pass authority."
      },
      {
        "title": "How to analyze a competitor's backlink profile",
        "to": "/guides/how-to-analyze-competitor-backlinks",
        "description": "Find which of these sources already link to rivals."
      },
      {
        "title": "How to find and remove toxic backlinks",
        "to": "/guides/find-and-remove-toxic-backlinks",
        "description": "Recognize the \"backlink sites\" that hurt more than help."
      },
      {
        "title": "Types of backlinks, with examples",
        "to": "/guides/types-of-backlinks",
        "description": "What each source actually gives you, and what it is worth."
      },
      {
        "title": "How to get backlinks",
        "to": "/guides/how-to-get-backlinks",
        "description": "The method for earning links from the sources below."
      }
    ],
    "slug": "where-to-get-backlinks",
    "readingTime": "3 min read"
  }
]